import React, { useState } from "react";
import DemoDialog from "@/components/DemoDialog";
import { Search, ArrowRight, Play, Menu, X, Eye, TrendingUp, DollarSign, ShieldCheck, Truck, Wrench, Cpu, BarChart3, Wifi, AlertTriangle, CheckCircle2, Bell, Boxes, Layers, Globe, RefreshCw, Linkedin, Youtube, Twitter } from "lucide-react";

const RED = "#c00000";
const O = { fontFamily: "Outfit, sans-serif" };
const BG = "#0a0b0d";
const CARD = "#141518";
const LAPTOP = "https://static.prod-images.emergentagent.com/jobs/528f7e82-ffb7-429b-85f8-2b71023cc462/images/afd2ce3eeed518d7d3535778d2aec2e76e13e40a6898e4a2abaa6f934cb5e89b.jpeg";
const VAN = "https://images.unsplash.com/photo-1688054005456-466170816196?w=700&q=80";
const EXCAVATOR = "https://images.unsplash.com/photo-1780417944090-5f66a20e3390?w=700&q=80";
const SCANNER = "https://images.unsplash.com/photo-1633118599122-601a3ebc8699?w=700&q=80";

const NAV = ["Plattform", "Lösungen", "Integrationen", "Ressourcen", "Preise"];

function LogoB({ small }) {
  return (
    <span className="flex items-center gap-2">
      <svg viewBox="0 0 32 32" className={small ? "w-6 h-6" : "w-8 h-8"} aria-hidden>
        <path d="M5 27 L16 5 L20 5 L11 27 Z" fill={RED} />
        <path d="M14 27 L24 8 L28 8 L20 27 Z" fill={RED} opacity="0.65" />
      </svg>
      <span className={`${small ? "text-base" : "text-xl"} font-extrabold tracking-tight text-white`} style={O}>Fleet<span style={{ color: RED }}>Area</span></span>
    </span>
  );
}

export default function EntwurfB() {
  const [demo, setDemo] = useState(false);
  const [open, setOpen] = useState(false);
  const openDemo = () => setDemo(true);

  return (
    <div className="min-h-screen text-gray-200" style={{ background: BG, fontFamily: "Inter, sans-serif" }}>
      {/* HEADER */}
      <header className="sticky top-0 z-40 border-b border-white/10" style={{ background: `${BG}e6`, backdropFilter: "blur(10px)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <a href="#" data-testid="b-logo"><LogoB /></a>
          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-gray-400">
            {NAV.map((n) => <a key={n} href="#" className="hover:text-white transition-colors">{n}</a>)}
          </nav>
          <div className="hidden lg:flex items-center gap-5">
            <button aria-label="Suche" className="text-gray-400 hover:text-white transition-colors"><Search className="w-5 h-5" /></button>
            <button onClick={openDemo} className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Login</button>
            <button data-testid="b-cta-header" onClick={openDemo} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white rounded-lg px-4 py-2 hover:brightness-110 transition-all" style={{ background: RED }}>Loslegen <ArrowRight className="w-4 h-4" /></button>
          </div>
          <button className="lg:hidden text-white" onClick={() => setOpen((v) => !v)} data-testid="b-mobile-toggle">{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/10 px-4 py-4 space-y-1">
            {NAV.map((n) => <a key={n} href="#" onClick={() => setOpen(false)} className="block py-2 text-gray-300">{n}</a>)}
            <button onClick={() => { setOpen(false); openDemo(); }} className="w-full mt-2 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ background: RED }}>Loslegen</button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute top-0 right-0 w-[55%] h-full" style={{ background: `radial-gradient(circle at 75% 35%, ${RED}40, transparent 60%)` }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-bold tracking-[0.22em] text-gray-500 mb-5" style={O}>ALLE ASSETS. EINE PLATTFORM.</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.03] text-white" style={O}>
              Jedes Asset überwachen.<br /><span style={{ color: RED }}>Überall.</span>
            </h1>
            <p className="mt-6 text-base text-gray-400 max-w-lg leading-relaxed">FleetArea gibt Ihnen vollständige Sichtbarkeit und Kontrolle über Fahrzeuge, Maschinen, IT-Geräte und mehr – in einer sicheren Plattform für den Enterprise-Betrieb.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="b-hero-cta" onClick={openDemo} className="inline-flex items-center justify-center gap-2 text-white font-bold rounded-xl px-6 py-3.5 text-sm hover:brightness-110 transition-all shadow-lg" style={{ background: RED, boxShadow: `0 12px 40px -12px ${RED}` }}>Loslegen <ArrowRight className="w-4 h-4" /></button>
              <button onClick={openDemo} className="inline-flex items-center justify-center gap-2 border border-white/20 hover:bg-white/5 text-white font-semibold rounded-xl px-6 py-3.5 text-sm transition-colors"><Play className="w-4 h-4 fill-current" /> Video ansehen</button>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400">
              {[[Eye, "Volle Transparenz"], [TrendingUp, "Höhere Verfügbarkeit"], [DollarSign, "Niedrigere Kosten"], [ShieldCheck, "Enterprise-ready"]].map(([Icon, x]) => (
                <span key={x} className="flex items-center gap-1.5"><Icon className="w-4 h-4" style={{ color: RED }} /> {x}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div aria-hidden className="absolute -inset-6 rounded-3xl blur-2xl opacity-40" style={{ background: `radial-gradient(circle at 60% 40%, ${RED}, transparent 70%)` }} />
            <div className="relative rounded-2xl border border-white/10 shadow-2xl overflow-hidden" style={{ background: "#000" }}>
              <img src={LAPTOP} alt="FleetArea Dashboard" data-testid="b-hero-image" className="w-full h-auto block" loading="eager" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
          <div className="text-[11px] font-bold tracking-[0.2em] text-gray-600" style={O}>ECHTE ASSETS · ECHTER BETRIEB · EIN EFFIZIENTERES MORGEN</div>
        </div>
      </section>

      {/* BRANCHEN */}
      <section className="py-8 px-4 sm:px-6 border-y border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[11px] font-bold tracking-[0.2em] text-gray-600 mb-5">EINGESETZT IN VERSCHIEDENEN BRANCHEN</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {["Logistik", "Bauwirtschaft", "Fertigung", "Luftfahrt", "Handel", "IT & Facility", "u. v. m."].map((b) => (
              <span key={b} className="text-sm font-semibold text-gray-500 tracking-tight" style={O}>{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ASSET LANDSCAPE */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs font-bold tracking-[0.2em] mb-3" style={{ color: RED }}>EINE PLATTFORM · JEDER ASSET-TYP</div>
          <div className="grid lg:grid-cols-2 gap-4 mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={O}>Gebaut für Ihre gesamte Asset-Landschaft.</h2>
            <p className="text-gray-400 self-end">Von Fahrzeugen auf der Straße über Maschinen im Feld bis zu IT-Geräten im Büro – FleetArea vereint alle Assets, Daten und Workflows.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[[Truck, "Fahrzeuge", "Standort, Auslastung, Verbrauch und Servicestatus in Echtzeit.", VAN], [Wrench, "Maschinen", "Betriebsstunden, Zustand, Wartung und Einsatzort im Blick.", EXCAVATOR], [Cpu, "IT-Geräte", "Laptops, Mobilgeräte und Infrastruktur mit automatischen Updates.", SCANNER], [BarChart3, "Analytics", "Aus Asset-Daten werden Erkenntnisse. Leistung steigern, Ausfälle senken.", LAPTOP]].map(([Icon, t, d, img], i) => (
              <div key={i} data-testid={`b-asset-${i}`} className="group rounded-2xl overflow-hidden border border-white/10" style={{ background: CARD }}>
                <div className="relative h-36 overflow-hidden">
                  <img src={img} alt={t} className="w-full h-full object-cover grayscale opacity-70 transition-all duration-500 group-hover:opacity-90 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${CARD}, transparent 70%)` }} />
                  <span className="absolute bottom-3 left-3 w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: RED }}><Icon className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} /></span>
                </div>
                <div className="p-4">
                  <h3 className="text-base font-bold text-white mb-1.5" style={O}>{t}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTEGRATE / DEPLOY */}
      <section className="py-16 px-4 sm:px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] mb-3 text-gray-500" style={O}>NAHTLOSE ANBINDUNG</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={O}>Integrieren. Erweitern. Skalieren.</h2>
            <p className="mt-3 text-gray-400">Verbinden Sie FleetArea mit Ihren bestehenden Tools und Systemen. Offene Plattform und APIs machen die Anbindung einfach.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Telematik", "IT-Management", "ERP & Finanzen", "ServiceDesk", "Weitere"].map((x) => (
                <span key={x} className="text-xs font-semibold text-gray-300 rounded-lg px-3 py-2 border border-white/10" style={{ background: CARD }}>{x}</span>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-gray-600">Beispielhafte Integrations-Kategorien · Demovorschau</p>
          </div>
          <div>
            <div className="text-xs font-bold tracking-[0.2em] mb-3 text-gray-500" style={O}>ÜBERALL BEREITSTELLEN</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={O}>Remote-Agent-Rollout.</h2>
            <p className="mt-3 text-gray-400">Agenten auf allen Geräten installieren und aktualisieren – weltweit. Sicher, leichtgewichtig und stets verbunden.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[[RefreshCw, "Automatischer Rollout"], [ShieldCheck, "Sicher & leichtgewichtig"], [Layers, "Plattformübergreifend"], [Globe, "Global skalierbar"]].map(([Icon, x]) => (
                <span key={x} className="flex items-center gap-2 text-sm text-gray-300"><Icon className="w-4 h-4" style={{ color: RED }} /> {x}</span>
              ))}
            </div>
            <button onClick={openDemo} className="mt-6 inline-flex items-center gap-1.5 text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:brightness-110 transition-all" style={{ background: RED }}>Mehr erfahren <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative py-20 px-4 sm:px-6 overflow-hidden" style={{ background: `linear-gradient(120deg, ${BG} 40%, ${RED})` }}>
        <div className="relative max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] text-white/70 mb-3" style={O}>EIN VERNETZTERES MORGEN</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight max-w-2xl" style={O}>Machen Sie Ihre Assets zum Wettbewerbsvorteil.</h2>
            <p className="mt-3 text-white/80">FleetArea. Gebaut für das, was Ihr Geschäft bewegt.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button data-testid="b-cta-bottom" onClick={openDemo} className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-bold rounded-xl px-6 py-3.5 text-sm hover:bg-gray-100 transition-colors">Loslegen <ArrowRight className="w-4 h-4" /></button>
            <button onClick={openDemo} className="inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold rounded-xl px-6 py-3.5 text-sm hover:bg-white/10 transition-colors">Vertrieb kontaktieren</button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="text-gray-400 px-4 sm:px-6 py-12 border-t border-white/10" style={{ background: BG }}>
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
          <div><LogoB /><p className="text-xs text-gray-500 mt-3">Alle Assets. Ein effizienteres Morgen.</p></div>
          {[["Plattform", ["Übersicht", "Funktionen", "Integrationen", "Preise"]], ["Lösungen", ["Fahrzeuge", "Maschinen", "IT-Geräte", "Analytics"]], ["Ressourcen", ["Dokumentation", "Blog", "Status", "Support"]], ["Unternehmen", ["Über uns", "Karriere", "News", "Kontakt"]]].map(([h, items]) => (
            <div key={h}><div className="text-white font-semibold text-sm mb-3" style={O}>{h}</div><ul className="space-y-2 text-sm">{items.map((x) => <li key={x}><a href="#" className="hover:text-white transition-colors">{x}</a></li>)}</ul></div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <div className="flex items-center gap-4"><span className="font-mono">© 2026 FleetArea</span><span>Datenschutz</span><span>AGB</span></div>
          <div className="flex items-center gap-3"><Linkedin className="w-4 h-4" /><Youtube className="w-4 h-4" /><Twitter className="w-4 h-4" /></div>
        </div>
      </footer>

      <DemoDialog open={demo} onClose={() => setDemo(false)} dark />
    </div>
  );
}
